import { TFunction } from '@lyearn/i18n';
import { QuestionSubTypeEnum, QuestionTypeEnum, UpdateQuestionEntity } from '@/types/schema';

import { QuizCreatorQuestionsErrors } from '../../hooks/useQuizEditor/types';
import validateFillInTheBlanks from './fill-in-the-blanks';
import validateImageHotspot from './image-hotspot';
import validateMatchTheFollowing from './match-the-following';
import validatePictureChoiceQuestion from './picture-choice';

export const validateQuestions = (
  t: TFunction,
  questions: UpdateQuestionEntity[],
  isQuestionSubWorkspacesEnabled: boolean,
) => {
  const errors: QuizCreatorQuestionsErrors = {};
  questions.forEach((question) => {
    const error = validateQuizQuestion({ t, question, isQuestionSubWorkspacesEnabled });

    if (!error) {
      return;
    }

    errors[question.id] = error;
  });
  return errors;
};

export const validateQuizQuestion = ({
  t,
  question,
  isQuestionSubWorkspacesEnabled,
}: {
  t: TFunction;
  question?: UpdateQuestionEntity;
  isQuestionSubWorkspacesEnabled: boolean;
}) => {
  if (!question?.questionText) {
    return {
      message: t('QuizCreator:Validations.QuestionCannotBeEmpty'),
      type: 'QuestionCannotBeEmpty',
    };
  }

  if (isQuestionSubWorkspacesEnabled && !question.subWorkspaceIds?.set.length) {
    return {
      message: t('QuizCreator:Validations.WorkspaceSettingsEmpty'),
      type: 'SubWorkspaceCannotBeEmpty',
    };
  }

  const options = question?.options?.set || [];

  if (question.questionType === QuestionTypeEnum.MatchTheFollowing) {
    const error = validateMatchTheFollowing(t, question);
    if (error) {
      return error;
    }
  } else if (question.questionType === QuestionTypeEnum.FillInTheBlanks) {
    const error = validateFillInTheBlanks(t, question);
    if (error) {
      return error;
    }
  } else if (question.questionType === QuestionTypeEnum.ImageHotspot) {
    const error = validateImageHotspot(t, question);
    if (error) {
      return error;
    }
  } else {
    if (options.length < 2) {
      return { message: t('QuizCreator:Validations.OptionLengthError'), type: 'OptionLengthError' };
    }

    const emptyOptions = options.filter((option) => !option.optionText);
    if (emptyOptions.length) {
      const isCorrectOptionEmpty = emptyOptions.length === 1 && emptyOptions[0].optionEvaluation;
      const message = isCorrectOptionEmpty
        ? t('QuizCreator:Validations.CorrectOptionCannotBeEmpty')
        : t('QuizCreator:Validations.OptionCannotBeEmpty', { count: emptyOptions.length });

      return { message, type: 'OptionCannotBeEmpty' };
    }

    // Skip correct option validation for WEIGHTED_OPTION subType
    if (question.subType !== QuestionSubTypeEnum.WeightedOption) {
      const correctOptionsCount = options.filter((option) => option.optionEvaluation).length;
      if (correctOptionsCount === 0) {
        return {
          message: t('QuizCreator:Validations.OptionEvaluationError'),
          type: 'OptionEvaluationError',
        };
      }
    }
  }

  // Picture-Choice specific validations
  if (question.questionType === QuestionTypeEnum.PictureMultipleChoiceQuestion) {
    const error = validatePictureChoiceQuestion(t, options);
    if (error) {
      return error;
    }
  }
};
